import { formatDate } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ChargeConfig } from '../models/charge-config';
import { ChargeFilter } from '../models/charge-filter';
import { ChargeLinked } from '../models/charge-linked';
import { ChargeResult } from '../models/charge-result';
import { ChargeSync } from '../models/charge-sync';
import { DistributionFilter } from '../models/distribution-filter';

@Injectable({
    providedIn: 'root',
})
export class ChargesDistributionService {

    constructor(private http: HttpClient) { }

    getFilterChargeConfigs(cnpj: string): Observable<ChargeConfig> {
        if (cnpj === '') {
            return of();
        }

        return this.http
            .get<ChargeConfig>(`${environment.cobranca_novo}/charges/configuration/${cnpj}/list`)
            .pipe(catchError(err => of(err)));
    }

    getCharges(cnpj: string, chargeFilter: ChargeFilter, page: number, limit: number): Observable<ChargeResult> {
        if (cnpj === '') {
            return of();
        }

        let bodyFilter: DistributionFilter = {};

        if (chargeFilter != null) {
            if (chargeFilter.dueDate.fromDate !== null && chargeFilter.dueDate.toDate !== null) {
                bodyFilter.venctoIni = formatDate(chargeFilter.dueDate.fromDate, 'yyyyMMdd', 'pt-br');
                bodyFilter.venctoFim = formatDate(chargeFilter.dueDate.toDate, 'yyyyMMdd', 'pt-br');
            }
            if (chargeFilter.scheduling.fromDate !== null && chargeFilter.scheduling.toDate !== null) {
                bodyFilter.dt_agendaIni = formatDate(chargeFilter.scheduling.fromDate, 'yyyyMMdd', 'pt-br');
                bodyFilter.dt_agendaFim = formatDate(chargeFilter.scheduling.toDate, 'yyyyMMdd', 'pt-br');
            }
            if (chargeFilter.schedulingCollector.fromDate !== null && chargeFilter.schedulingCollector.toDate !== null) {
                bodyFilter.date_toIni = formatDate(chargeFilter.schedulingCollector.fromDate, 'yyyy-MM-dd', 'pt-br');
                bodyFilter.date_toFim = formatDate(chargeFilter.schedulingCollector.toDate, 'yyyy-MM-dd', 'pt-br');
            }
            if (chargeFilter.collector.length > 0) {
                bodyFilter.cobrador = chargeFilter.collector[0].codigo_externo;
            }
            if (chargeFilter.uf.length > 0) {
                bodyFilter.estados = chargeFilter.uf.map(uf => uf.estado);
            }
            if (chargeFilter.city.length > 0) {
                bodyFilter.municipios = chargeFilter.city.map(city => city.municipio);
            }
            if (chargeFilter.contracts.length > 0) {
                bodyFilter.contratos = chargeFilter.contracts.map(contract => contract.value);
            }
            if (chargeFilter.clients.length > 0) {
                bodyFilter.nomes = chargeFilter.clients.map(client => client.value);
            }
            if (chargeFilter.neighborhoods.length > 0) {
                bodyFilter.bairros = chargeFilter.neighborhoods.map(neighborhood => neighborhood.bairro);
            }
            if (chargeFilter.status !== null && chargeFilter.status !== '') {
                if (chargeFilter.status === 'ativos') bodyFilter.status_contratos = ['A'];
                else if (chargeFilter.status === 'suspensos') bodyFilter.status_contratos = ['S'];
                else if (chargeFilter.status === 'ambos') bodyFilter.status_contratos = ['A', 'S'];
            }
            if (chargeFilter.overdueInstallments.start !== null && chargeFilter.overdueInstallments.end !== null) {
                bodyFilter.atrasoMin = chargeFilter.overdueInstallments.start;
                bodyFilter.atrasoMax = chargeFilter.overdueInstallments.end;
            }
            if (chargeFilter.homeCharge) {
                bodyFilter.forma_pagamento = 'DO';
            }
            if (chargeFilter.areaCode && chargeFilter.areaCode !== '') {
                bodyFilter.codigo_area = chargeFilter.areaCode;
            }
        }

        return this.http
            .post<ChargeResult>(`${environment.cobranca_novo}/charges/${cnpj}/${limit}/${page}/list`, bodyFilter)
            .pipe(catchError(err => of(err)));
    }

    linkCharges(cnpj: string, chargesLinked: ChargeLinked[]) {
        if (cnpj === '' || chargesLinked.length <= 0) {
            return of();
        }

        return this.http
            .post<{ ok: string }>(`${environment.cobranca_novo}/charges/linked/${cnpj}/save`, chargesLinked)
            .pipe(catchError(err => of(err)));
    }

    syncChargeByCgc(cnpj: string, cgc: string): Observable<ChargeSync> {
        if (cnpj === '') {
            return of({ error: true, msg: 'CNPJ não informado.' });
        }

        if (cgc === '' || cgc === undefined || cgc === null) {
            return of({ error: true, msg: 'CGC não informado.' });
        }

        return this.http
            .put<ChargeSync>(`${environment.cobranca_novo}/sync/charges/${cnpj}/${cgc}/update`, {})
            .pipe(catchError(err => of(err)));
    }

    getChargeAreas(cnpj: string, limit: number, page: number): Observable<{ result: { descricao_area: string; codigo_area: string }[] }> {
        return this.http.get<{ result: { descricao_area: string; codigo_area: string }[] }>(
            `${environment.cobranca_novo}/integration/charges_areas/${cnpj}/${limit}/${page}`
        );
    }
}
